import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../App';
import { GridSkeleton, EmptyState, NotionBlockRenderer, PageHeader } from '../components/shared';
import { BookOpen, FileText, ChevronLeft, ExternalLink } from 'lucide-react';

const DOC_ICONS = {
  'CRM': '📋',
  'PET-400': '⚙️',
  'Alarm': '🔔',
  'Vet Portal': '🏥',
  'Relationship': '🔗',
};

function getDocIcon(title) {
  for (const [key, icon] of Object.entries(DOC_ICONS)) {
    if (title.includes(key)) return icon;
  }
  return '📄';
}

function getDocDescription(title) {
  if (title.includes('CRM')) return 'Guide for using the Pure Souls CRM platform';
  if (title.includes('Operator')) return 'Operating procedures for the PET-400 system';
  if (title.includes('Alarm')) return 'Directory of PET-400 alarm codes and responses';
  if (title.includes('Relationship')) return 'How CRM and Vet Portal systems connect';
  if (title.includes('Vet Portal')) return 'Admin panel guide for the Vet Portal';
  return 'Reference documentation';
}

export default function ReferenceDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docContent, setDocContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/reference`)
      .then(r => r.json())
      .then(d => { setDocs(d.documents || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const openDoc = async (doc) => {
    setSelectedDoc(doc);
    setContentLoading(true);
    setDocContent(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/reference/${doc.id}`);
      const data = await res.json();
      setDocContent(data.blocks || []);
    } catch { }
    setContentLoading(false);
  };

  return (
    <div className="page-container" data-testid="reference-documents">
      <PageHeader title="Reference Documents" subtitle="Manuals, guides, and operational references" />

      {loading ? <GridSkeleton count={5} /> : docs.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {docs.map(doc => (
            <div
              key={doc.id}
              className="ps-card p-6 cursor-pointer"
              onClick={() => openDoc(doc)}
              data-testid={`reference-doc-card-${doc.id}`}
            >
              <div className="text-3xl mb-4">{getDocIcon(doc.title)}</div>
              <h3 className="text-sm font-semibold mb-2">{doc.title}</h3>
              <p className="text-xs text-muted-foreground">{getDocDescription(doc.title)}</p>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={BookOpen} title="No documents" description="Reference documents will appear here." />
      )}

      {/* Side Panel */}
      {selectedDoc && (
        <>
          <div className={`side-panel-overlay ${selectedDoc ? 'open' : ''}`} onClick={() => setSelectedDoc(null)} />
          <div className={`side-panel ${selectedDoc ? 'open' : ''}`} data-testid="reference-doc-detail-panel">
            <div className="sticky top-0 bg-card z-10 p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <button onClick={() => setSelectedDoc(null)} className="p-2 rounded-lg hover:bg-muted" data-testid="panel-close-button">
                  <ChevronLeft size={20} />
                </button>
              </div>
              <div className="mt-3">
                <div className="text-2xl mb-2">{getDocIcon(selectedDoc.title)}</div>
                <h2 className="text-lg font-bold">{selectedDoc.title}</h2>
              </div>
            </div>
            <div className="p-6">
              {contentLoading ? (
                <div className="space-y-3">
                  {[1,2,3,4,5,6].map(i => <div key={i} className="h-4 bg-muted rounded animate-skeleton" style={{ width: `${50 + Math.random() * 50}%` }} />)}
                </div>
              ) : docContent ? (
                <NotionBlockRenderer blocks={docContent} />
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
